import { useEffect, useRef, useState } from 'react';
import { type ElementEventBasePayload, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { type HorizontalEdge, isDragSourceData, isDropTargetData, isNewPanelDragData } from '../common/panel-dnd';
import { createPanel } from '../common/panel-factory';
import { getPanelDropPreview, type PanelDropPreview, resolveDrop } from '../common/panel-grid';
import { type PageCmsPanel, type PageCmsPanels } from '../common/panel-types';

const getHorizontalEdge = (targetData: Record<string | symbol, unknown>): HorizontalEdge | null => {
  const edge = extractClosestEdge(targetData);
  return edge === 'left' || edge === 'right' ? edge : null;
};

const isSamePreview = (a: PanelDropPreview | null, b: PanelDropPreview | null): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.rowIndex === b.rowIndex &&
    a.incomingPanelId === b.incomingPanelId &&
    a.row.length === b.row.length &&
    a.row.every((panel, index) => panel.id === b.row[index].id));

/**
 * The single owner of drag commits. Sources and targets only report where the pointer is; this maps
 * (source, innermost target, closest edge) to a hover projection and, on release, to the next grid.
 */
export const usePanelDragMonitor = (panels: PageCmsPanels, onDrop: (next: PageCmsPanels) => void) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [dropPreview, setDropPreview] = useState<PanelDropPreview | null>(null);
  const newPanelRef = useRef<PageCmsPanel | null>(null);

  // `panels` only changes on commit and `onDrop` is stable, so the monitor is never re-registered mid-drag.
  useEffect(() => {
    const updateDropPreview = ({ source, location }: ElementEventBasePayload) => {
      const targetData = location.current.dropTargets[0]?.data;
      const next =
        isDragSourceData(source.data) && targetData && isDropTargetData(targetData)
          ? getPanelDropPreview(panels, source.data, targetData, getHorizontalEdge(targetData), newPanelRef.current)
          : null;

      setDropPreview((previous) => (isSamePreview(previous, next) ? previous : next));
    };

    return monitorForElements({
      canMonitor: ({ source }) => isDragSourceData(source.data),
      onDragStart: (payload) => {
        // Created once so the projected id and the committed id are identical.
        newPanelRef.current = isNewPanelDragData(payload.source.data)
          ? createPanel(payload.source.data.typename)
          : null;
        setIsDragActive(true);
        updateDropPreview(payload);
      },
      onDropTargetChange: updateDropPreview,
      onDrag: updateDropPreview,
      onDrop: ({ source, location }) => {
        setIsDragActive(false);
        setDropPreview(null);

        const newPanel = newPanelRef.current;
        newPanelRef.current = null;
        const target = location.current.dropTargets[0];

        if (!target || !isDragSourceData(source.data) || !isDropTargetData(target.data)) {
          return;
        }

        const next = resolveDrop(
          panels,
          source.data,
          target.data,
          getHorizontalEdge(target.data),
          (typename) => newPanel ?? createPanel(typename)
        );

        if (next !== panels) {
          onDrop(next);
        }
      }
    });
  }, [panels, onDrop]);

  return { isDragActive, dropPreview };
};
